import { useState } from "react";
import { studioAPI, tryParseJSON } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { QUIZ } from "@/constants/testIds";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Copy } from "lucide-react";
import AskDirector from "@/components/AskDirector";

const STYLES = ["animals", "humor", "story", "gaming", "education", "asmr"];
const AUDIENCES = ["kids", "teens", "adults", "seniors", "all"];

export default function ConceptQuiz() {
    const { lang } = useApp();
    const [daily, setDaily] = useState(30);
    const [onCamera, setOnCamera] = useState("no");
    const [style, setStyle] = useState("animals");
    const [audience, setAudience] = useState("adults");
    const [busy, setBusy] = useState(false);
    const [raw, setRaw] = useState("");
    const parsed = tryParseJSON(raw);

    const run = async () => {
        setBusy(true); setRaw("");
        try {
            const { result } = await studioAPI.quiz({ daily_minutes: daily, on_camera: onCamera, style, audience, language: lang });
            setRaw(result);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid={QUIZ.container} className="grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-2 space-y-5">
                <div>
                    <div className="overline">01 · Quiz</div>
                    <h2 className="font-display text-2xl font-black uppercase mt-1">{lang === "fr" ? "Trouve ta niche" : "Find your niche"}</h2>
                    <p className="text-sm text-slate-400 mt-2">{lang === "fr" ? "Réponds à 4 questions pour obtenir un concept complet + 30 idées." : "Answer 4 questions to get a full concept + 30 ideas."}</p>
                    <div className="mt-3">
                        <AskDirector
                            testid="quiz-ask-director"
                            prompt={lang === "fr" ? "Je ne sais pas quelle niche choisir. Peux-tu me guider ?" : lang === "es" ? "No sé qué nicho elegir. ¿Puedes guiarme?" : "I don't know which niche to pick. Can you guide me?"}
                            label={lang === "fr" ? "Besoin d'aide pour choisir ?" : lang === "es" ? "¿Necesitas ayuda para elegir?" : "Need help picking?"}
                        />
                    </div>
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Temps quotidien" : "Daily time"}: <span className="text-[#FF4D00] font-mono-sf">{daily}min</span></label>
                    <input data-testid={QUIZ.daily} type="range" min="5" max="180" step="5" value={daily} onChange={(e) => setDaily(+e.target.value)}
                        className="w-full mt-2 accent-[#FF4D00]" />
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Apparaître à l'écran ?" : "On camera?"}</label>
                    <div data-testid={QUIZ.onCamera} className="mt-2 grid grid-cols-2 gap-2">
                        {["yes", "no"].map((v) => (
                            <button key={v} onClick={() => setOnCamera(v)}
                                className={`py-2 rounded border text-sm font-medium kinetic ${onCamera === v ? "border-[#FF4D00] bg-[#FF4D00]/10 text-white" : "border-[#253046] text-slate-400 hover:text-white"}`}>
                                {v === "yes" ? (lang === "fr" ? "Oui" : "Yes") : (lang === "fr" ? "Non" : "No")}
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Style" : "Style"}</label>
                    <select data-testid={QUIZ.style} value={style} onChange={(e) => setStyle(e.target.value)}
                        className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]">
                        {STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>

                <div>
                    <label className="text-xs uppercase tracking-widest text-slate-500">{lang === "fr" ? "Audience" : "Audience"}</label>
                    <select data-testid={QUIZ.audience} value={audience} onChange={(e) => setAudience(e.target.value)}
                        className="mt-2 w-full bg-[#0F141C] border border-[#253046] rounded px-3 py-2.5 text-sm focus:outline-none focus:border-[#FF4D00]">
                        {AUDIENCES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>

                <button data-testid={QUIZ.generateBtn} onClick={run} disabled={busy}
                    className="w-full flame-gradient text-black font-semibold py-3 rounded flex items-center justify-center gap-2 kinetic flame-glow disabled:opacity-60">
                    {busy ? "···" : <>{lang === "fr" ? "Générer mon blueprint" : "Generate my blueprint"} <ArrowRight className="w-4 h-4" /></>}
                </button>
            </div>

            <div data-testid={QUIZ.result} className="lg:col-span-3 rounded border border-[#253046] bg-[#0B111B] p-5 min-h-[500px]">
                {!raw && !busy && (
                    <div className="h-full flex flex-col items-center justify-center text-center py-20">
                        <Sparkles className="w-10 h-10 text-slate-700 mb-3" />
                        <p className="text-sm text-slate-500">{lang === "fr" ? "Lance le quiz pour voir apparaître ton concept ici." : "Run the quiz to see your concept here."}</p>
                    </div>
                )}
                {busy && <div className="h-full flex items-center justify-center text-slate-400 font-mono-sf text-xs">GENERATING BLUEPRINT ···</div>}
                {parsed && (
                    <div className="space-y-5">
                        <div>
                            <div className="overline">Concept</div>
                            <div className="font-display text-2xl font-black uppercase mt-1">{parsed.channel_name || parsed.concept}</div>
                            <p className="text-sm text-slate-300 mt-2">{parsed.concept}</p>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {["positioning", "tone", "visual_style", "target_audience"].map((k) => parsed[k] && (
                                <div key={k} className="bg-[#161D2A] border border-[#253046] rounded p-3">
                                    <div className="overline">{k.replace("_", " ")}</div>
                                    <div className="text-sm text-slate-200 mt-1">{parsed[k]}</div>
                                </div>
                            ))}
                        </div>
                        {parsed.character && (
                            <div className="bg-gradient-to-br from-[#1E2738] to-[#161D2A] border border-[#FF4D00]/30 rounded p-4">
                                <div className="overline">Character</div>
                                <div className="text-sm text-slate-200 mt-2 whitespace-pre-wrap">
                                    {typeof parsed.character === "string" ? parsed.character : JSON.stringify(parsed.character, null, 2)}
                                </div>
                            </div>
                        )}
                        {parsed.ideas?.length && (
                            <div>
                                <div className="flex items-center justify-between">
                                    <div className="overline">{parsed.ideas.length} ideas</div>
                                    <button onClick={() => { navigator.clipboard.writeText(parsed.ideas.join("\n")); toast.success("Copié"); }} className="text-xs text-slate-400 hover:text-white flex items-center gap-1"><Copy className="w-3 h-3" /> Copy</button>
                                </div>
                                <ol className="mt-3 grid sm:grid-cols-2 gap-1.5 text-sm">
                                    {parsed.ideas.map((idea, i) => (
                                        <li key={i} className="bg-[#161D2A] border border-[#253046] rounded px-2.5 py-2 flex gap-2">
                                            <span className="font-mono-sf text-[10px] text-[#FF4D00] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                                            <span className="text-slate-200 leading-snug">{idea}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        )}
                    </div>
                )}
                {raw && !parsed && (
                    <pre className="text-xs text-slate-300 whitespace-pre-wrap">{raw}</pre>
                )}
            </div>
        </div>
    );
}
